import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import HomePage from './pages/HomePage';
import AdminGuard from './components/AdminGuard';
import AdminUsersPage from './pages/admin/UsersPage';
import { pdf } from '@react-pdf/renderer';
import { ChevronDown, ChevronRight, Download, TriangleAlert } from 'lucide-react';
import Layout from './components/Layout';
import Header from './components/Header';
import ProjectOverview from './components/ProjectOverview';
import Snapshot from './components/Snapshot';
import TabControl from './components/TabControl';
import WhyWe from './components/WhyWe';
import TrustedBy from './components/TrustedBy';
import TopSupportingEvidence from './components/TopSupportingEvidence';
import ProjectCriteriaFit from './components/ProjectCriteriaFit';
import RelevantProjectExperience, { type RelevantProjectExperienceItem } from './components/RelevantProjectExperience';
import IntroQualifications from './components/IntroQualifications';
import OnePagerPdf from './components/OnePagerPdf';
import SaveBar from './components/SaveBar';
import InterimOnePager, { formatLocationCityState, normalizeWebsiteUrl } from './components/InterimOnePager';
import InterimOnePagerPdf from './components/InterimOnePagerPdf';
import SendEmailModal from './components/SendEmailModal';
import Toast from './components/Toast';
import ProcessWaitingOverlay from './components/ProcessWaitingOverlay';
import { rootService } from './services/rootService';
import { ApiError, apiClient } from './services/apiClient';
import { generationsService } from './services/generationsService';
import {
  onePagersService,
  type OnePagerContent,
  type OnePagerType,
  type OnePagerVersionsResponse,
} from './services/onePagersService';
import {
  salesforceService,
  type SalesforceAccountResponse,
  type SalesforceProviderOnePagerMetadataResponse,
} from './services/salesforceService';
import { htmlToPlainText, snapshotHtmlToAlignmentPoints } from './utils/html';
import { magicLinkService } from './services/magicLinkService';
import { resumesService, type ProviderResumeResponse } from './services/resumesService';
import { formatAsteriskBoldToHtml } from './utils/emphasis';
import headerBg from './assets/header-bg.png';
import testVersion from './testVersion.json';
import './App.css';

const SkeletonBlock = ({ className }: { className: string }) => <div className={`animate-pulse rounded bg-gray-200 ${className}`} />;

async function fetchImageAsDataUrl(url: string): Promise<string> {
  const res = await fetch(url, { headers: { Accept: 'image/png,image/jpeg,image/webp,image/svg+xml,image/*' } });
  if (!res.ok) {
    throw new Error(`Failed to fetch image: ${res.status} ${res.statusText}`);
  }

  const contentType = (res.headers.get('content-type') ?? '').toLowerCase();
  if (!contentType.startsWith('image/')) {
    throw new Error(`Unexpected content-type: ${contentType || 'unknown'}`);
  }

  const toDataUrl = async (blob: Blob) => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read image blob'));
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.readAsDataURL(blob);
    });
    return dataUrl;
  };

  if (contentType.includes('image/svg')) {
    const svgText = await res.text();
    const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load svg into Image'));
      el.src = svgDataUrl;
    });

    const width = Math.max(1, img.naturalWidth || 120);
    const height = Math.max(1, img.naturalHeight || 80);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2d context');
    }
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/png');
  }

  if (contentType.includes('image/webp')) {
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Failed to load webp into Image'));
        el.src = objectUrl;
      });

      const width = Math.max(1, img.naturalWidth || 120);
      const height = Math.max(1, img.naturalHeight || 80);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get 2d context');
      }
      ctx.drawImage(img, 0, 0, width, height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  const blob = await res.blob();
  return toDataUrl(blob);
}

function getLoadedHeaderLogoProxyUrl(): string {
  const el = document.getElementById('provider-logo-img') as HTMLImageElement | null;
  const src = el?.currentSrc || el?.src;
  if (!src) return '';

  try {
    const u = new URL(src);
    if (u.hostname === 'cdn.brandfetch.io') {
      return `/brandfetch${u.pathname}`;
    }
    if (u.hostname === 'logo.clearbit.com') {
      return `/clearbit${u.pathname}`;
    }
  } catch {
    // ignore
  }

  return '';
}

const RelevantProjectExperienceSkeleton = () => (
  <div id="section-relevant-project-experience">
    <div className="w-full bg-white p-[59px]">
      <SkeletonBlock className="h-[20px] w-[280px]" />
      <div className="mt-[16px] space-y-[15px]">
        <div className="h-[116px] rounded-[10px] border border-[#dbdbdb]" />
        <div className="h-[116px] rounded-[10px] border border-[#dbdbdb]" />
        <div className="h-[116px] rounded-[10px] border border-[#dbdbdb]" />
      </div>
      <div className="mt-[22px] h-[2px] w-full bg-[#E8E8E8]" />
    </div>
  </div>
);

type DraftState = {
  snapshotHtml?: string;
  whyWeHtml?: string;
  projectOverviewHtml?: string;
  criteriaFitByKey?: Record<string, string>;
  highlightedEvidence?: OnePagerContent['highlighted_evidence'];
};

export const buildNextContent = (
  baseContent: OnePagerContent,
  draft: DraftState,
  metadataCriteria?: Record<string, string>
): OnePagerContent => {
  const nextSnapshotHtml = draft.snapshotHtml !== undefined ? draft.snapshotHtml : baseContent.snapshot_html;
  const nextWhyWeHtml = draft.whyWeHtml !== undefined ? draft.whyWeHtml : baseContent.fit_explanation_html;
  const nextProjectOverviewHtml =
    draft.projectOverviewHtml !== undefined ? draft.projectOverviewHtml : baseContent.project_overview_html;

  const nextHighlightedEvidence =
    draft.highlightedEvidence !== undefined ? draft.highlightedEvidence : baseContent.highlighted_evidence;

  const baseCriteriaFit =
    baseContent.criteria_fit ??
    (metadataCriteria
      ? Object.entries(metadataCriteria)
          .filter(([, v]) => !!v)
          .map(([k, v]) => ({ criterion_key: k, criterion_value: v, confidence: 0, explanation: '', explanation_html: undefined }))
      : undefined);

  return {
    ...baseContent,
    key_alignment_points:
      nextSnapshotHtml !== undefined ? snapshotHtmlToAlignmentPoints(nextSnapshotHtml) : baseContent.key_alignment_points,
    snapshot_html: nextSnapshotHtml,
    project_overview_html: nextProjectOverviewHtml,
    project_overview: nextProjectOverviewHtml !== undefined ? htmlToPlainText(nextProjectOverviewHtml) : baseContent.project_overview,
    fit_explanation: nextWhyWeHtml !== undefined ? htmlToPlainText(nextWhyWeHtml) : baseContent.fit_explanation,
    fit_explanation_html: nextWhyWeHtml,
    highlighted_evidence: nextHighlightedEvidence,
    criteria_fit:
      baseCriteriaFit?.map((item) => {
        const nextExplanationHtml = draft.criteriaFitByKey?.[item.criterion_key];
        if (nextExplanationHtml === undefined) return item;
        return {
          ...item,
          explanation: htmlToPlainText(nextExplanationHtml),
          explanation_html: nextExplanationHtml,
        };
      }) ?? baseCriteriaFit,
  };
};


const getGenerationStorageKey = (accountId: string, introductionId: string, userId?: string) =>
  `onePagerGenerationRequestId:${accountId}:${introductionId}${userId ? `:${userId}` : ''}`;

type LaunchExchangeResponse = {
  userId: string;
  purpose?: string;
  context: {
    accountId: string;
    introductionId: string;
  };
};

const ONE_PAGER_PATH = '/one-pager';
const LAUNCH_CONTEXT_STORAGE_KEY = 'onePagerLaunchContext:v1';

const readLaunchContextFromStorage = (): { accountId: string; introductionId: string; userId: string } | undefined => {
  try {
    const raw = window.sessionStorage.getItem(LAUNCH_CONTEXT_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { accountId?: unknown; introductionId?: unknown; userId?: unknown };
    if (typeof parsed.accountId !== 'string') return undefined;
    if (typeof parsed.introductionId !== 'string') return undefined;
    if (typeof parsed.userId !== 'string') return undefined;
    return { accountId: parsed.accountId, introductionId: parsed.introductionId, userId: parsed.userId };
  } catch {
    return undefined;
  }
};

const writeLaunchContextToStorage = (ctx: { accountId: string; introductionId: string; userId: string }) => {
  try {
    window.sessionStorage.setItem(LAUNCH_CONTEXT_STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    return;
  }
};

// Dev/demo override until the Markup concierge type exists in Salesforce:
// append ?onePagerType=interim to force the interim view.
const readOnePagerTypeOverride = (): OnePagerType | undefined => {
  const raw = new URLSearchParams(window.location.search).get('onePagerType');
  return raw === 'interim' || raw === 'group' ? raw : undefined;
};

const replaceUrlRemovingToken = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  const nextSearch = url.searchParams.toString();
  const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`;
  window.history.replaceState({}, '', nextUrl);
};

const splitDelimitedList = (raw: string) =>
  raw
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter((s) => !!s);

const getInitials = (name: string) => {
  const withoutParens = (name ?? '').replace(/\([^)]*\)/g, ' ');
  const parts = withoutParens
    .split(/\s+/)
    .map((p) => p.trim())
    .filter((p) => /[A-Za-z]/.test(p));

  if (parts.length === 0) return 'OP';

  if (parts.length === 1) {
    const caps = parts[0].match(/[A-Z]/g) ?? [];
    if (caps.length >= 2) return caps.join('').slice(0, 3);
    const letters = parts[0].match(/[A-Za-z]/g) ?? [];
    return letters.slice(0, 2).map((l) => l.toUpperCase()).join('') || 'OP';
  }

  const initials = parts.flatMap((word) => {
    const caps = word.match(/[A-Z]/g) ?? [];
    if (caps.length >= 2) return caps;
    return [(word.match(/[A-Za-z]/)?.[0] ?? '').toUpperCase()].filter(Boolean);
  });
  return initials.join('').slice(0, 3) || 'OP';
};

const toSafeFileNamePart = (raw: string) =>
  raw
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const getShortDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getFormattedDateParts = (isoString: string) => {
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) {
    return { date: '', time: '' };
  }

  const date = d.toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  return { date, time };
};

function LandingPage() {
  const [query, setQuery] = useState<{ accountId?: string; introductionId?: string; userId?: string }>({});
  const [launch, setLaunch] = useState<{ loading: boolean; error?: string }>({ loading: false });
  const launchExchangeRef = useRef<{ token: string; inFlight: boolean; completed: boolean } | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [sfProviderMetadata, setSfProviderMetadata] = useState<{
    loading: boolean;
    error?: string;
    value?: SalesforceProviderOnePagerMetadataResponse;
  }>({ loading: false });
  const [salesforceAccount, setSalesforceAccount] = useState<{
    loading: boolean;
    error?: string;
    value?: SalesforceAccountResponse;
  }>({ loading: false });
  const [resumeInfo, setResumeInfo] = useState<{
    loading: boolean;
    error?: string;
    value?: ProviderResumeResponse | null;
  }>({ loading: false });
  const [onePagerVersions, setOnePagerVersions] = useState<{
    loading: boolean;
    error?: string;
    value?: OnePagerVersionsResponse;
  }>({ loading: false });
  const [draft, setDraft] = useState<DraftState>({});
  const [saving, setSaving] = useState<{ loading: boolean; error?: string }>({ loading: false });
  const [publishing, setPublishing] = useState<{ loading: boolean; error?: string }>({ loading: false });
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [magicLinkUrl, setMagicLinkUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [pdfGeneration, setPdfGeneration] = useState<{
    versionId?: string;
    status?: 'processing' | 'success' | 'failed' | null;
    error?: string | null;
    blobUrl?: string | null;
  }>({});
  const [saveCommitId, setSaveCommitId] = useState(0);
  const [usedIntroQualSequences, setUsedIntroQualSequences] = useState<number[]>([]);
  const [pendingIntroQualAdd, setPendingIntroQualAdd] = useState<{ generation: number; content: string; sequence: number } | null>(null);
  const introQualAddGenerationRef = useRef(0);
  const [generation, setGeneration] = useState<{
    loading: boolean;
    error?: string;
    requestId?: string;
    status?: 'pending' | 'processing' | 'success' | 'failed';
  }>({ loading: false });
  const [pollMeta, setPollMeta] = useState<{ count: number; latestStatus?: string }>({ count: 0 });
  const [pdfDownload, setPdfDownload] = useState<{ loading: boolean; error?: string }>({ loading: false });
  const [clientProjectDescription, setClientProjectDescription] = useState<{
    loading: boolean;
    value?: string;
    error?: boolean;
  }>({ loading: false });
  const [clientProjectDescriptionRetry, setClientProjectDescriptionRetry] = useState(0);
  const [showLogo, setShowLogo] = useState(true);
  const [toggleLogoLoading, setToggleLogoLoading] = useState(false);
  const showLogoInitializedRef = useRef(false);

  const headerCompanyName = salesforceAccount.value?.name ?? '';
  const headerLocation = (() => {
    const { city, state } = salesforceAccount.value?.billingAddress ?? {};
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return '';
  })();
  const headerWebsiteText = salesforceAccount.value?.website ?? '';

  const trustedByFirmNames = (() => {
    const raw = salesforceAccount.value?.peClients;
    if (!raw) return [];
    return raw
      .split(/[;,]/)
      .map((s) => s.trim())
      .filter((s) => !!s);
  })();

  const trustedByPeReviewQuote = salesforceAccount.value?.peReview ?? '';

  const headerServicesText = (() => {
    const services = salesforceAccount.value?.services;
    if (!services) return '';

    const values = Object.values(services).flatMap((v) => (v ? splitDelimitedList(v) : []));

    if (values.length === 0) return '';

    return values.join(', ');
  })();

  const headerIndustriesText = (() => {
    const industriesRaw = salesforceAccount.value?.industries;
    if (!industriesRaw) return '';

    const industries = splitDelimitedList(industriesRaw);

    if (industries.length === 0) return '';

    return industries.join(', ');
  })();

  useEffect(() => {
    if (process.env.REACT_APP_IS_DEV === 'true') {
      setQuery({ accountId: testVersion.accountId, introductionId: testVersion.introductionId, userId: testVersion.userId });
      setLaunch({ loading: false });
      return;
    }

    const search = new URLSearchParams(window.location.search);
    const token = search.get('token');
    const legacyAccountId = search.get('accountId');
    const legacyIntroductionId = search.get('introductionId');

    if (token) {
      const existing = launchExchangeRef.current;
      if (existing?.token === token) {
        if (existing.completed) return;
        if (existing.inFlight) return;
      }

      launchExchangeRef.current = { token, inFlight: true, completed: false };
      const controller = new AbortController();
      setLaunch({ loading: true });

      (async () => {
        try {
          const res = await apiClient.postJson<LaunchExchangeResponse>(
            '/api/v1/auth/launch/exchange',
            { token },
            { signal: controller.signal }
          );
          const next = {
            accountId: res.context.accountId,
            introductionId: res.context.introductionId,
            userId: res.userId,
          };
          writeLaunchContextToStorage(next);
          setQuery(next);
          replaceUrlRemovingToken();
          setLaunch({ loading: false });

          if (launchExchangeRef.current?.token === token) {
            launchExchangeRef.current = { token, inFlight: false, completed: true };
          }
        } catch (e: unknown) {
          if (e instanceof DOMException && e.name === 'AbortError') {
            if (launchExchangeRef.current?.token === token) {
              launchExchangeRef.current = { token, inFlight: false, completed: false };
            }
            return;
          }

          let message = e instanceof Error ? e.message : String(e);
          if (e instanceof ApiError && e.bodyText) {
            try {
              const parsed = JSON.parse(e.bodyText) as { message?: string };
              if (parsed?.message) {
                message = `${message}: ${parsed.message}`;
              } else {
                message = `${message}: ${e.bodyText}`;
              }
            } catch {
              message = `${message}: ${e.bodyText}`;
            }
          }

          setLaunch({ loading: false, error: message });
          setQuery({});

          if (launchExchangeRef.current?.token === token) {
            launchExchangeRef.current = { token, inFlight: false, completed: false };
          }
        }
      })();

      return () => {
        controller.abort();
        if (launchExchangeRef.current?.token === token && launchExchangeRef.current.inFlight) {
          launchExchangeRef.current = { token, inFlight: false, completed: false };
        }
      };
    }

    const stored = readLaunchContextFromStorage();
    if (stored) {
      setQuery(stored);
      return;
    }

    if (legacyAccountId && legacyIntroductionId) {
      setQuery({ accountId: legacyAccountId, introductionId: legacyIntroductionId });
      setLaunch({ loading: false });
      return;
    }

    setLaunch({ loading: false, error: 'Missing launch token. Please launch from Salesforce again.' });
  }, []);

  useEffect(() => {
    if (!query.accountId || !query.introductionId) {
      return;
    }

    const controller = new AbortController();
    setSfProviderMetadata({ loading: true });
    setSalesforceAccount({ loading: true });

    (async () => {
      try {
        const data = await salesforceService.getProviderOnePagerMetadata(query.accountId!, query.introductionId!, {
          signal: controller.signal,
        });
        setSfProviderMetadata({ loading: false, value: data });
        setSalesforceAccount({ loading: false, value: data.account });
        console.log('[Salesforce] provider one-pager metadata', data);
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        const message = e instanceof Error ? e.message : String(e);
        setSfProviderMetadata({ loading: false, error: message });
        setSalesforceAccount({ loading: false, error: message });
        console.error('[Salesforce] provider one-pager metadata failed', e);
      } finally {
        setSfProviderMetadata((prev) => ({ ...prev, loading: false }));
        setSalesforceAccount((prev) => ({ ...prev, loading: false }));
      }
    })();

    return () => controller.abort();
  }, [query.accountId, query.introductionId, query.userId]);

  useEffect(() => {
    const concierge = sfProviderMetadata.value?.concierge;
    if (!concierge || concierge.clientProjectDescription != null) {
      return;
    }

    const controller = new AbortController();
    setClientProjectDescription({ loading: true });

    (async () => {
      try {
        const res = await salesforceService.generateClientProjectDescription(concierge.id, {
          signal: controller.signal,
        });
        setClientProjectDescription({ loading: false, value: res.description });
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        console.error('[Salesforce] client project description generation failed', e);
        setClientProjectDescription({ loading: false, error: true });
      }
    })();

    return () => controller.abort();
  }, [sfProviderMetadata.value?.concierge, clientProjectDescriptionRetry]);

  useEffect(() => {
    const conciergeId = sfProviderMetadata.value?.concierge?.id;
    if (!conciergeId) return;
    const controller = new AbortController();
    magicLinkService.getByConciergeId(conciergeId)
      .then(({ url }) => setMagicLinkUrl(url))
      .catch(() => {/* non-critical, button simply won't render */});
    return () => controller.abort();
  }, [sfProviderMetadata.value?.concierge?.id]);

  useEffect(() => {
    if (!query.accountId || !query.introductionId) {
      return;
    }

    const controller = new AbortController();
    setOnePagerVersions({ loading: true });

    (async () => {
      try {
        const res = await onePagersService.listVersions(query.accountId!, query.introductionId!, {
          signal: controller.signal,
        });
        setOnePagerVersions({ loading: false, value: res });

        if (res.versions.length > 0) {
          return;
        }

        const storageKey = getGenerationStorageKey(query.accountId!, query.introductionId!, query.userId);
        const existingRequestId = window.localStorage.getItem(storageKey) ?? undefined;
        if (existingRequestId) {
          setGeneration({ loading: false, requestId: existingRequestId, status: 'pending' });
          return;
        }

        setGeneration({ loading: true });
        const createRes = await generationsService.create(
          {
            accountId: query.accountId!,
            introductionId: query.introductionId!,
            userId: query.userId,
          },
          { signal: controller.signal }
        );

        window.localStorage.setItem(storageKey, createRes.requestId);
        setGeneration({ loading: false, requestId: createRes.requestId, status: createRes.status });
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        const message = e instanceof Error ? e.message : String(e);
        setOnePagerVersions({ loading: false, error: message });
        setGeneration({ loading: false, error: message });
      } finally {
        setOnePagerVersions((prev) => ({ ...prev, loading: false }));
        setGeneration((prev) => ({ ...prev, loading: false }));
      }
    })();

    return () => controller.abort();
  }, [query.accountId, query.introductionId, query.userId]);

  useEffect(() => {
    if (!query.accountId || !query.introductionId) {
      return;
    }

    if (!generation.requestId) {
      return;
    }

    const controller = new AbortController();
    let intervalId: number | undefined;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;

      try {
        const res = await generationsService.getStatus(generation.requestId!, { signal: controller.signal });
        setPollMeta((prev) => ({ count: prev.count + 1, latestStatus: res.status }));
        setGeneration((prev) => ({ ...prev, status: res.status, error: res.error }));

        if (res.status === 'failed') {
          stopped = true;
          if (intervalId) window.clearInterval(intervalId);

          const storageKey = getGenerationStorageKey(query.accountId!, query.introductionId!, query.userId);
          window.localStorage.removeItem(storageKey);
          return;
        }

        if (res.status !== 'success') {
          return;
        }

        stopped = true;
        if (intervalId) window.clearInterval(intervalId);

        const storageKey = getGenerationStorageKey(query.accountId!, query.introductionId!, query.userId);
        window.localStorage.removeItem(storageKey);

        const versions = await onePagersService.listVersions(query.accountId!, query.introductionId!, {
          signal: controller.signal,
        });
        setOnePagerVersions({ loading: false, value: versions });
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        const message = e instanceof Error ? e.message : String(e);
        setGeneration((prev) => ({ ...prev, error: message }));
      }
    };

    poll();
    intervalId = window.setInterval(poll, 5000);

    return () => {
      stopped = true;
      controller.abort();
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [generation.requestId, query.accountId, query.introductionId, query.userId]);

  useEffect(() => {
    if (generation.requestId && generation.status && generation.status !== 'success' && generation.status !== 'failed') {
      return;
    }

    setPollMeta({ count: 0 });
  }, [generation.requestId, generation.status]);

  useEffect(() => {
    if (pdfGeneration.status === 'success') {
      setToast('PDF generated successfully!');
    }
  }, [pdfGeneration.status]);



  const latestOnePager = (() => {
    const versions = onePagerVersions.value?.versions;
    if (!versions || versions.length === 0) return undefined;
    return versions.reduce((acc, v) => (v.version > acc.version ? v : acc), versions[0]);
  })();

  useEffect(() => {
    if (latestOnePager && !showLogoInitializedRef.current) {
      showLogoInitializedRef.current = true;
      setShowLogo(latestOnePager.showLogo ?? true);
    }
  }, [latestOnePager]);

  useEffect(() => {
    const effectiveType: OnePagerType =
      readOnePagerTypeOverride() ?? onePagerVersions.value?.onePagerType ?? latestOnePager?.onePagerType ?? 'group';

    if (!query.accountId || effectiveType !== 'interim') {
      return;
    }

    const controller = new AbortController();
    setResumeInfo({ loading: true });

    resumesService
      .getProviderResume(query.accountId, { signal: controller.signal })
      .then((res) => setResumeInfo({ loading: false, value: res }))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        const message = e instanceof Error ? e.message : String(e);
        setResumeInfo({ loading: false, error: message, value: null });
        console.error('[Resumes] provider resume fetch failed', e);
      });

    return () => controller.abort();
  }, [query.accountId, onePagerVersions.value?.onePagerType, latestOnePager?.onePagerType]);

  const publishedVersions = useMemo(() => {
    const versions = onePagerVersions.value?.versions;
    if (!versions || versions.length === 0) return [];
    return versions
      .filter((v) => (v.status ?? '').toLowerCase() === 'published')
      .sort((a, b) => b.version - a.version);
  }, [onePagerVersions.value]);

  const latestPublishedVersion = publishedVersions[0];
  const hasPublishedVersion = !!latestPublishedVersion;

  const hasDraftPendingWhilePublished = (() => {
    if (!hasPublishedVersion) return false;
    if (!latestOnePager) return false;
    return (latestOnePager.status ?? '').toLowerCase() !== 'published';
  })();

  const hasDraftChanges =
    draft.snapshotHtml !== undefined ||
    draft.whyWeHtml !== undefined ||
    draft.projectOverviewHtml !== undefined ||
    (draft.criteriaFitByKey !== undefined && Object.keys(draft.criteriaFitByKey).length > 0) ||
    draft.highlightedEvidence !== undefined;

  const statusLabel = hasPublishedVersion
    ? hasDraftChanges
      ? 'Draft (Modified/Unpublished Changes)'
      : hasDraftPendingWhilePublished
        ? 'Published (Draft Pending)'
        : 'Published'
    : 'Draft';

  const recordName = headerCompanyName || 'One-Pager';
  const clientName = sfProviderMetadata.value?.concierge?.companyName || '<Client>';

  const publishDisabledBecauseAlreadyPublished =
    hasPublishedVersion &&
    !hasDraftPendingWhilePublished &&
    (latestOnePager?.status ?? '').toLowerCase() === 'published';

  const [savedProjectOverviewHtml, setSavedProjectOverviewHtml] = useState<string | undefined>(undefined);

  const projectOverviewBodyHtml = (() => {
    if (savedProjectOverviewHtml !== undefined) return savedProjectOverviewHtml;
    const html = latestOnePager?.content?.project_overview_html;
    if (html) return html;
    const text = latestOnePager?.content?.project_overview;
    if (text) return `<p>${formatAsteriskBoldToHtml(text)}</p>`;
    // clientProjectDescription is stored as HTML when edited via the editor.
    // If it starts with a tag, use it directly; plain-text legacy values fall
    // through to the projectDescription prop which handles escaping.
    const sfHtml = sfProviderMetadata.value?.concierge?.clientProjectDescription?.trim();
    if (sfHtml?.startsWith('<')) return sfHtml;
    return undefined;
  })();

  const projectOverviewPdfHtml = (() => {
    if (draft.projectOverviewHtml !== undefined) return draft.projectOverviewHtml;
    if (projectOverviewBodyHtml) return projectOverviewBodyHtml;

    const raw = (
      sfProviderMetadata.value?.concierge?.clientProjectDescription ??
      clientProjectDescription.value
    )?.trim();
    if (!raw) return '';

    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const normalized = raw.replace(/<br\s*\/?>/gi, '\n');
    const blocks = normalized
      .split(/\r?\n\s*\r?\n/)
      .map((b) => b.trim())
      .filter((b) => !!b);

    if (blocks.length === 0) return '';
    return blocks.map((b) => `<p>${escapeHtml(b).replace(/\r?\n/g, '<br />')}</p>`).join('');
  })();

  const latestWarnings = latestOnePager?.content?.warnings;
  const primaryWarning = latestWarnings?.[0];
  const warningSeverityLabel = primaryWarning?.severity
    ? primaryWarning.severity.charAt(0).toUpperCase() + primaryWarning.severity.slice(1)
    : undefined;

  const highlightedEvidenceItems = useMemo(() => {
    const raw = latestOnePager?.content?.highlighted_evidence;
    const effective = draft.highlightedEvidence ?? raw;
    if (!effective || effective.length === 0) return [];

    const createdBy = latestOnePager?.createdBy;
    const isHumanCreated = !!createdBy && createdBy !== 'DORE-E';
    const ordered =
      draft.highlightedEvidence || isHumanCreated
        ? [...effective]
        : [...effective].sort((a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0));

    return ordered.map((e) => ({
      id: e.evidence_id,
      title: e.title,
      content: e.content,
      sourceId: e.source_id,
      evidenceType: e.evidence_type,
      relevanceScore: e.relevance_score,
    }));
  }, [latestOnePager, draft.highlightedEvidence]);

  const topSupportingEvidenceItems = highlightedEvidenceItems.slice(0, 2);
  const relevantProjectExperienceItems = highlightedEvidenceItems;

  const snapshotBodyHtmlFromApi = (() => {
    const snapshotHtml = latestOnePager?.content?.snapshot_html;
    if (snapshotHtml) return snapshotHtml;

    const rawPoints = latestOnePager?.content?.key_alignment_points;
    if (typeof rawPoints === 'string' && rawPoints.trim().startsWith('<')) {
      return rawPoints;
    }

    return undefined;
  })();

  const snapshotBulletsFromApi = (() => {
    const rawPoints = latestOnePager?.content?.key_alignment_points;
    const points = Array.isArray(rawPoints) ? rawPoints : undefined;
    if (!points || points.length === 0) return undefined;
    return points.map((p, idx) => ({
      label: '',
      body: p,
    }));
  })();

  const whyWeBodyHtml = (() => {
    const html = latestOnePager?.content?.fit_explanation_html;
    if (html) return html;
    const text = latestOnePager?.content?.fit_explanation;
    if (!text) return undefined;
    return `<p>${formatAsteriskBoldToHtml(text)}</p>`;
  })();

  const pdfSnapshotHtml = draft.snapshotHtml ?? snapshotBodyHtmlFromApi ?? '';

  const pdfWhyWeHtml = draft.whyWeHtml ?? whyWeBodyHtml ?? '';

  const saveBarCreatedBy = latestOnePager?.createdByName || 'unknown';
  const saveBarUpdatedAt = latestOnePager?.updatedAt;
  const saveBarDateParts = saveBarUpdatedAt ? getFormattedDateParts(saveBarUpdatedAt) : undefined;
  const saveBarLastEditedDate = saveBarDateParts?.date;
  const saveBarLastEditedTime = saveBarDateParts?.time;

  const CRITERIA_TYPE_TO_KEY: Record<string, string> = {
    price: 'budget',
    geography: 'location',
  };

  const projectCriteriaFitRows = (() => {
    const criteria = sfProviderMetadata.value?.criteria;
    if (!criteria || criteria.length === 0) return [];
    const criteriaFitLookup = Object.fromEntries(
      (latestOnePager?.content?.criteria_fit ?? []).map((item) => [item.criterion_key, item])
    );
    return [...criteria]
      .sort((a, b) => a.top - b.top)
      .filter((c) => !!c.definition)
      .map((criterion) => {
        const rawKey = criterion.type.toLowerCase().replace(/\s+/g, '_');
        const key = CRITERIA_TYPE_TO_KEY[rawKey] ?? rawKey;
        const fitItem = criteriaFitLookup[key];
        const providerFitHtml =
          fitItem?.explanation_html ??
          (fitItem?.explanation
            ? (() => {
                const explanation = fitItem.explanation;
                const isProbablyHtml = /<\s*[a-z][\s\S]*>/i.test(explanation);
                if (isProbablyHtml) {
                  const trimmed = explanation.trim();
                  return trimmed.startsWith('<') ? trimmed : `<p>${explanation}</p>`;
                }
                return `<p>${formatAsteriskBoldToHtml(explanation)}</p>`;
              })()
            : '<p></p>');
        return {
          criterionKey: key,
          label: criterion.type,
          yourNeeds: criterion.definition,
          providerFitHtml,
          providerFitText: fitItem?.explanation,
        };
      });
  })();

  const ensureMagicLinkUrl = async (): Promise<string | null> => {
    if (magicLinkUrl) return magicLinkUrl;
    const conciergeId = sfProviderMetadata.value?.concierge?.id;
    if (!conciergeId) return null;
    try {
      const { url } = await magicLinkService.getByConciergeId(conciergeId);
      setMagicLinkUrl(url);
      return url;
    } catch {
      return null;
    }
  };

  const handleToggleShowLogo = async () => {
    if (!latestOnePager || !query.accountId || !query.introductionId || !query.userId) return;
    const next = !showLogo;
    setToggleLogoLoading(true);
    try {
      const newVersion = await onePagersService.createVersion(query.accountId, query.introductionId, {
        content: latestOnePager.content as unknown as Record<string, unknown>,
        status: 'Draft',
        userId: query.userId,
        showLogo: next,
        initials: getInitials(headerCompanyName),
      });
      setShowLogo(next);
      setOnePagerVersions((prev) => {
        if (!prev.value) return prev;
        return {
          ...prev,
          value: {
            ...prev.value,
            versions: [newVersion, ...prev.value.versions],
          },
        };
      });
    } catch (e) {
      console.error('[Logo] toggle failed', e);
    } finally {
      setToggleLogoLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!latestOnePager) return;
    if (!query.accountId) {
      setPdfDownload({ loading: false, error: 'Missing launch context. Please launch from Salesforce again.' });
      return;
    }

    setPdfDownload({ loading: true });
    const ensuredMagicLinkUrl = await ensureMagicLinkUrl();
    try {
      const sfAccount = salesforceAccount.value ?? sfProviderMetadata.value?.account;
      if (!sfAccount) {
        throw new Error('Missing Salesforce account data. Wait for provider metadata to load, then try again.');
      }

      const pdfCompanyName = sfAccount?.name ?? '';
      const pdfLocation = (() => { const { city, state } = sfAccount?.billingAddress ?? {}; if (city && state) return `${city}, ${state}`; if (city) return city; if (state) return state; return ''; })();
      const pdfWebsiteText = sfAccount?.website ?? '';
      const pdfInitials = getInitials(pdfCompanyName);
      const pdfLogoSrc = (() => {
        const trimmed = (pdfWebsiteText ?? '').trim();
        if (!trimmed) return '';
        const normalized = trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
        try {
          const host = new URL(normalized).hostname;
          const domain = host.replace(/^www\./, '');
          if (!domain) return '';
          return `/brandfetch/${domain}/w/120/h/80`;
        } catch {
          return '';
        }
      })();

      const pdfLogoFallbackSrc = (() => {
        const trimmed = (pdfWebsiteText ?? '').trim();
        if (!trimmed) return '';
        const normalized = trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
        try {
          const host = new URL(normalized).hostname;
          const domain = host.replace(/^www\./, '');
          if (!domain) return '';
          return `/clearbit/${domain}`;
        } catch {
          return '';
        }
      })();

      const pdfLogoSrcEmbedded = await (async () => {
        const loadedFromHeader = getLoadedHeaderLogoProxyUrl();
        if (loadedFromHeader) {
          try {
            return await fetchImageAsDataUrl(loadedFromHeader);
          } catch {
            // fall through
          }
        }

        if (pdfLogoSrc) {
          try {
            return await fetchImageAsDataUrl(pdfLogoSrc);
          } catch {
            // fall through
          }
        }
        if (pdfLogoFallbackSrc) {
          try {
            return await fetchImageAsDataUrl(pdfLogoFallbackSrc);
          } catch {
            return '';
          }
        }
        return '';
      })();
      const pdfTrustedByFirmNames = (sfAccount?.peClients ?? '')
        .split(/[;,]/)
        .map((s) => s.trim())
        .filter((s) => !!s);

      const doc = (
        <OnePagerPdf
          headerBackgroundSrc={headerBg}
          logoSrc={showLogo ? pdfLogoSrcEmbedded : ''}
          initials={pdfInitials}
          isVerified={!!sfAccount}
          companyName={pdfCompanyName}
          location={pdfLocation}
          websiteText={pdfWebsiteText}
          projectOverviewTitle="Project Overview"
          projectOverviewHtml={projectOverviewPdfHtml}
          snapshotTitle="Snapshot - Why They're The Right Fit"
          snapshotBullets={(snapshotBulletsFromApi ?? []).map((b) => ({ label: b.label, body: b.body }))}
          snapshotHtml={pdfSnapshotHtml}
          whyWeTitle="Why we recommend them for your project"
          whyWeHtml={pdfWhyWeHtml}
          trustedByTitle="Trusted By Top PE Firms Like"
          trustedByFirmNames={pdfTrustedByFirmNames}
          peBackedReviewQuote={sfAccount?.peReview ?? ''}
          peBackedReviewAttribution=""
          topSupportingEvidenceTitle="Top Supporting Evidence"
          topSupportingEvidenceItems={topSupportingEvidenceItems.map((item) => ({
            title: item.title,
            content: item.content,
          }))}
          projectCriteriaFitTitle="Project Criteria Fit"
          projectCriteriaFitRows={projectCriteriaFitRows.map((r) => ({
            label: r.label,
            yourNeeds: r.yourNeeds,
            providerFit: r.providerFitText,
            providerFitHtml: r.providerFitHtml,
          }))}
          relevantProjectExperienceTitle="Relevant Project Experience"
          relevantProjectExperienceItems={relevantProjectExperienceItems.map((item) => ({
            title: item.title,
            content: item.content,
          }))}
          magicLinkUrl={ensuredMagicLinkUrl && query.introductionId ? `${ensuredMagicLinkUrl}?introductionId=${query.introductionId}&cta=get_introduced&utm_medium=pdf_action&utm_source=one_pager` : ensuredMagicLinkUrl ?? undefined}
          inviteTeamMemberUrl={ensuredMagicLinkUrl ? `${ensuredMagicLinkUrl}?cta=invite_team_member` : undefined}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeProviderName = toSafeFileNamePart(pdfCompanyName || 'Provider');
      a.download = `BluWave Provider Recommendation - ${safeProviderName} ${getShortDate()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setPdfDownload({ loading: false });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setPdfDownload({ loading: false, error: message });
      console.error('[PDF] download failed', e);
    }
  };

  const handleConfirmPublish = async () => {
    console.log('[SaveBar] Publish clicked', {
      accountId: query.accountId,
      introductionId: query.introductionId,
      hasLatestOnePager: !!latestOnePager,
      hasDraftChanges,
    });

    if (!query.accountId || !query.introductionId) {
      setPublishing({ loading: false, error: 'Missing launch context. Please launch from Salesforce again.' });
      return;
    }
    if (!latestOnePager) {
      setPublishing({ loading: false, error: 'No one-pager loaded yet. Please wait for content to load, then try again.' });
      return;
    }

    setPublishing({ loading: true });
    try {
      const baseContent: OnePagerContent = latestOnePager.content ?? {};
      const nextContent: OnePagerContent = hasDraftChanges ? buildNextContent(baseContent, draft, sfProviderMetadata.value?.criteria
          ? Object.fromEntries(sfProviderMetadata.value.criteria.map((c) => { const raw = c.type.toLowerCase().replace(/\s+/g, '_'); const key = CRITERIA_TYPE_TO_KEY[raw] ?? raw; return [key, c.definition]; }))
          : undefined) : baseContent;

      const publishedVersion = await onePagersService.createVersion(query.accountId, query.introductionId, {
        content: nextContent as unknown as Record<string, unknown>,
        status: 'Published',
        userId: query.userId,
        showLogo,
        initials: getInitials(headerCompanyName),
      });

      const { accountId, introductionId } = query;
      const versionId = publishedVersion.id;
      setPdfGeneration({ versionId, status: 'processing' });
      (async () => {
        try {
          const ensuredMagicLinkUrl = await ensureMagicLinkUrl();
          const sfAccount = salesforceAccount.value ?? sfProviderMetadata.value?.account;
          const pdfCompanyName = sfAccount?.name ?? '';
          const pdfWebsiteText = sfAccount?.website ?? '';
          const pdfLocation = (() => { const { city, state } = sfAccount?.billingAddress ?? {}; if (city && state) return `${city}, ${state}`; if (city) return city; if (state) return state; return ''; })();
          const pdfInitials = getInitials(pdfCompanyName);
          const pdfLogoSrc = (() => {
            const trimmed = pdfWebsiteText.trim();
            if (!trimmed) return '';
            const normalized = trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
            try {
              const domain = new URL(normalized).hostname.replace(/^www\./, '');
              return domain ? `/brandfetch/${domain}/w/120/h/80` : '';
            } catch { return ''; }
          })();
          const pdfLogoFallbackSrc = (() => {
            const trimmed = pdfWebsiteText.trim();
            if (!trimmed) return '';
            const normalized = trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
            try {
              const domain = new URL(normalized).hostname.replace(/^www\./, '');
              return domain ? `/clearbit/${domain}` : '';
            } catch { return ''; }
          })();
          const pdfLogoSrcEmbedded = await (async () => {
            const loadedFromHeader = getLoadedHeaderLogoProxyUrl();
            if (loadedFromHeader) { try { return await fetchImageAsDataUrl(loadedFromHeader); } catch {} }
            if (pdfLogoSrc) { try { return await fetchImageAsDataUrl(pdfLogoSrc); } catch {} }
            if (pdfLogoFallbackSrc) { try { return await fetchImageAsDataUrl(pdfLogoFallbackSrc); } catch {} }
            return '';
          })();
          const pdfTrustedByFirmNames = (sfAccount?.peClients ?? '').split(/[;,]/).map((s) => s.trim()).filter(Boolean);

          const doc = (
            <OnePagerPdf
              headerBackgroundSrc={headerBg}
              logoSrc={showLogo ? pdfLogoSrcEmbedded : ''}
              initials={pdfInitials}
              isVerified={!!sfAccount}
              companyName={pdfCompanyName}
              location={pdfLocation}
              websiteText={pdfWebsiteText}
              projectOverviewTitle="Project Overview"
              projectOverviewHtml={projectOverviewPdfHtml}
              snapshotTitle="Snapshot - Why They're The Right Fit"
              snapshotBullets={(snapshotBulletsFromApi ?? []).map((b) => ({ label: b.label, body: b.body }))}
              snapshotHtml={pdfSnapshotHtml}
              whyWeTitle="Why we recommend them for your project"
              whyWeHtml={pdfWhyWeHtml}
              trustedByTitle="Trusted By Top PE Firms Like"
              trustedByFirmNames={pdfTrustedByFirmNames}
              peBackedReviewQuote={sfAccount?.peReview ?? ''}
              peBackedReviewAttribution=""
              topSupportingEvidenceTitle="Top Supporting Evidence"
              topSupportingEvidenceItems={topSupportingEvidenceItems.map((item) => ({ title: item.title, content: item.content }))}
              projectCriteriaFitTitle="Project Criteria Fit"
              projectCriteriaFitRows={projectCriteriaFitRows.map((r) => ({
                label: r.label,
                yourNeeds: r.yourNeeds,
                providerFit: r.providerFitText,
                providerFitHtml: r.providerFitHtml,
              }))}
              relevantProjectExperienceTitle="Relevant Project Experience"
              relevantProjectExperienceItems={relevantProjectExperienceItems.map((item) => ({ title: item.title, content: item.content }))}
              magicLinkUrl={ensuredMagicLinkUrl && query.introductionId ? `${ensuredMagicLinkUrl}?introductionId=${query.introductionId}&cta=get_introduced&utm_medium=pdf_action&utm_source=one_pager` : ensuredMagicLinkUrl ?? undefined}
              inviteTeamMemberUrl={ensuredMagicLinkUrl ? `${ensuredMagicLinkUrl}?cta=invite_team_member` : undefined}
            />
          );

          const blob = await pdf(doc).toBlob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const safeProviderName = toSafeFileNamePart(pdfCompanyName || 'Provider');
          const fileName = `BluWave Provider Recommendation - ${safeProviderName} ${getShortDate()}.pdf`;
          const pdfRes = await onePagersService.triggerPdfGeneration(accountId, introductionId, versionId, base64, fileName);
          setPdfGeneration({ versionId, status: 'success', blobUrl: pdfRes.blobUrl });
        } catch (e) {
          console.error('[PDF] generation failed', e);
          setPdfGeneration({ versionId, status: 'failed', error: 'PDF generation failed.' });
        }
      })();

      const res = await onePagersService.listVersions(query.accountId, query.introductionId);
      setOnePagerVersions({ loading: false, value: res });

      setDraft({});
      setSaveCommitId((prev) => prev + 1);
      setPublishing({ loading: false });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      console.error('[SaveBar] Publish failed', e);
      setPublishing({ loading: false, error: message });
    }
  };

  const handlePublishClick = () => {
    if (publishDisabledBecauseAlreadyPublished) return;
    setPublishConfirmOpen(true);
  };

  const handleRegenerateClick = () => {
    setRegenerateConfirmOpen(true);
  };

  const handleConfirmRegenerate = async () => {
    if (!query.accountId || !query.introductionId) return;
    try {
      const res = await generationsService.create({
        accountId: query.accountId,
        introductionId: query.introductionId,
        userId: query.userId,
        regenerateFromVersion: latestOnePager?.version,
      });
      const storageKey = getGenerationStorageKey(query.accountId, query.introductionId, query.userId);
      window.localStorage.setItem(storageKey, res.requestId);
      setGeneration({ loading: false, requestId: res.requestId, status: res.status });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setGeneration((prev) => ({ ...prev, error: message }));
    }
  };

  const isPollingActive =
    !!generation.requestId &&
    (generation.loading || generation.status === 'pending' || generation.status === 'processing') &&
    !!query.accountId &&
    !!query.introductionId;

  const handleRetryInterimGeneration = async () => {
    if (!query.accountId || !query.introductionId) return;
    try {
      setGeneration({ loading: true });
      const createRes = await generationsService.create({
        accountId: query.accountId,
        introductionId: query.introductionId,
        userId: query.userId,
      });
      const storageKey = getGenerationStorageKey(query.accountId, query.introductionId, query.userId);
      window.localStorage.setItem(storageKey, createRes.requestId);
      setGeneration({ loading: false, requestId: createRes.requestId, status: createRes.status });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setGeneration({ loading: false, error: message });
    }
  };

  // Interim one-pager content isn't diffed against a local draft (BPP-148): sections are generated,
  // not hand-assembled from group-flow fields, so save/publish round-trip the version's content as-is.
  const handleInterimSaveDraft = async () => {
    if (!query.accountId || !query.introductionId || !latestOnePager) {
      setSaving({ loading: false, error: 'Missing launch context. Please launch from Salesforce again.' });
      return;
    }
    setSaving({ loading: true });
    try {
      await onePagersService.createVersion(query.accountId, query.introductionId, {
        content: latestOnePager.content as unknown as Record<string, unknown>,
        status: 'Draft',
        userId: query.userId,
        initials: getInitials(headerCompanyName),
      });
      const res = await onePagersService.listVersions(query.accountId, query.introductionId);
      setOnePagerVersions({ loading: false, value: res });
      setSaving({ loading: false });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setSaving({ loading: false, error: message });
    }
  };

  const handleDismissWarning = async (warning: { category?: string; message: string }) => {
    if (!query.accountId || !query.introductionId || !latestOnePager) return;
    try {
      const updated = await onePagersService.dismissWarning(
        query.accountId,
        query.introductionId,
        latestOnePager.id,
        warning,
      );
      setOnePagerVersions((prev) => {
        if (!prev.value) return prev;
        return {
          ...prev,
          value: {
            ...prev.value,
            versions: prev.value.versions.map((v) => (v.id === updated.id ? updated : v)),
          },
        };
      });
    } catch (e: unknown) {
      console.error('[Warnings] dismiss failed', e);
    }
  };

  const buildInterimPdfDoc = () => {
    const sfAccount = salesforceAccount.value ?? sfProviderMetadata.value?.account;
    const fullName = sfAccount?.name ?? '';
    const firstName = fullName.split(' ')[0] || fullName;
    const content = latestOnePager?.content;
    const availability = sfProviderMetadata.value?.availability;
    const fees = sfProviderMetadata.value?.fees;

    return (
      <InterimOnePagerPdf
        fullName={fullName}
        tagline={sfAccount?.tagline}
        location={formatLocationCityState(content?.location?.base_city || sfAccount?.location)}
        linkedinUrl={sfAccount?.website ? normalizeWebsiteUrl(sfAccount.website) : undefined}
        resumeUrl={content?.resume_url ?? resumeInfo.value?.resumeUrl ?? undefined}
        generalBioHtml={content?.general_bio}
        snapshotTitle="Snapshot — Why They're The Right Fit"
        snapshotHtml={content?.snapshot}
        whyWeTitle={`Why we recommend ${firstName || 'them'}`}
        whyWeHtml={content?.why_we_recommend}
        startDate={availability?.start?.value}
        onsiteCommitment={availability?.onsite?.value}
        engagementRate={fees?.value}
      />
    );
  };

  const handleInterimDownloadPdf = async () => {
    if (!latestOnePager) return;
    setPdfDownload({ loading: true });
    try {
      const sfAccount = salesforceAccount.value ?? sfProviderMetadata.value?.account;
      const blob = await pdf(buildInterimPdfDoc()).toBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeProviderName = toSafeFileNamePart(sfAccount?.name || 'Provider');
      a.download = `BluWave Interim Provider One-Pager - ${safeProviderName} ${getShortDate()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setPdfDownload({ loading: false });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setPdfDownload({ loading: false, error: message });
      console.error('[PDF] interim download failed', e);
    }
  };

  const handleInterimPublish = async () => {
    if (!query.accountId || !query.introductionId) {
      setPublishing({ loading: false, error: 'Missing launch context. Please launch from Salesforce again.' });
      return;
    }
    if (!latestOnePager) {
      setPublishing({ loading: false, error: 'No one-pager loaded yet. Please wait for content to load, then try again.' });
      return;
    }
    setPublishing({ loading: true });
    try {
      const publishedVersion = await onePagersService.createVersion(query.accountId, query.introductionId, {
        content: latestOnePager.content as unknown as Record<string, unknown>,
        status: 'Published',
        userId: query.userId,
        initials: getInitials(headerCompanyName),
      });

      const { accountId, introductionId } = query;
      const versionId = publishedVersion.id;
      setPdfGeneration({ versionId, status: 'processing' });
      (async () => {
        try {
          const blob = await pdf(buildInterimPdfDoc()).toBlob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const sfAccount = salesforceAccount.value ?? sfProviderMetadata.value?.account;
          const safeProviderName = toSafeFileNamePart(sfAccount?.name || 'Provider');
          const fileName = `BluWave Interim Provider One-Pager - ${safeProviderName} ${getShortDate()}.pdf`;
          const pdfRes = await onePagersService.triggerPdfGeneration(accountId, introductionId, versionId, base64, fileName);
          setPdfGeneration({ versionId, status: 'success', blobUrl: pdfRes.blobUrl });
        } catch (e) {
          console.error('[PDF] interim generation failed', e);
          setPdfGeneration({ versionId, status: 'failed', error: 'PDF generation failed.' });
        }
      })();

      const res = await onePagersService.listVersions(query.accountId, query.introductionId);
      setOnePagerVersions({ loading: false, value: res });
      setPublishing({ loading: false });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setPublishing({ loading: false, error: message });
    }
  };

  const handleSaveDraft = async () => {
    console.log('[SaveBar] Save clicked', {
      accountId: query.accountId,
      introductionId: query.introductionId,
      hasLatestOnePager: !!latestOnePager,
      hasDraftChanges,
    });

    if (!query.accountId || !query.introductionId) {
      setSaving({ loading: false, error: 'Missing launch context. Please launch from Salesforce again.' });
      return;
    }
    setSaving({ loading: true });
    try {
      const baseContent: OnePagerContent = latestOnePager?.content ?? {};
      const nextContent: OnePagerContent = buildNextContent(baseContent, draft, sfProviderMetadata.value?.criteria
          ? Object.fromEntries(sfProviderMetadata.value.criteria.map((c) => { const raw = c.type.toLowerCase().replace(/\s+/g, '_'); const key = CRITERIA_TYPE_TO_KEY[raw] ?? raw; return [key, c.definition]; }))
          : undefined);

      await onePagersService.createVersion(query.accountId, query.introductionId, {
        content: nextContent as unknown as Record<string, unknown>,
        status: 'Draft',
        userId: query.userId,
        initials: getInitials(headerCompanyName),
      });

      console.log('[SaveBar] Save succeeded');

      const res = await onePagersService.listVersions(query.accountId, query.introductionId);
      setOnePagerVersions({ loading: false, value: res });

      setDraft({});
      setSaveCommitId((prev) => prev + 1);
      setSaving({ loading: false });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      console.error('[SaveBar] Save failed', e);
      setSaving({ loading: false, error: message });
    }
  };

  const handleSaveProjectOverview = async (html: string) => {
    if (!query.introductionId) throw new Error('No introduction ID available');
    await salesforceService.updateConciergeProjectDescription(query.introductionId, html);
    setSavedProjectOverviewHtml(html);
  };

  const handleCreateDraftVersionFromEvidence = async (items: RelevantProjectExperienceItem[]) => {
    if (!query.accountId || !query.introductionId) return;
    const newEvidence: OnePagerContent['highlighted_evidence'] = items.map((i) => ({
      evidence_id: i.id,
      title: i.title,
      content: i.content,
      source_id: i.sourceId ?? '',
      evidence_type: i.evidenceType ?? '',
      relevance_score: i.relevanceScore ?? 0,
    }));
    const baseContent: OnePagerContent = latestOnePager?.content ?? {};
    const nextContent = buildNextContent(baseContent, { ...draft, highlightedEvidence: newEvidence }, sfProviderMetadata.value?.criteria
          ? Object.fromEntries(sfProviderMetadata.value.criteria.map((c) => { const raw = c.type.toLowerCase().replace(/\s+/g, '_'); const key = CRITERIA_TYPE_TO_KEY[raw] ?? raw; return [key, c.definition]; }))
          : undefined);
    try {
      await onePagersService.createVersion(query.accountId, query.introductionId, {
        content: nextContent as unknown as Record<string, unknown>,
        status: 'Draft',
        userId: query.userId,
        initials: getInitials(headerCompanyName),
      });
      const res = await onePagersService.listVersions(query.accountId, query.introductionId);
      setOnePagerVersions({ loading: false, value: res });
      setDraft((prev) => {
        const { highlightedEvidence: _, ...rest } = prev;
        return rest;
      });
    } catch (e) {
      console.error('[RelevantProjectExperience] Auto-save version failed', e);
    }
  };

  const pdfHeaderAction = (
    <button
      type="button"
      className="inline-flex h-[56px] items-center gap-[12px] rounded-[12px] border border-[#132543] border-[0.5px] bg-white px-[32px] disabled:cursor-not-allowed disabled:opacity-60"
      onClick={handleDownloadPdf}
      disabled={pdfDownload.loading || clientProjectDescription.loading}
    >
      <Download size={20} color="#132543" />
      <span className="font-['Hanken_Grotesk',sans-serif] text-[16px] font-bold leading-[24px] tracking-[0.4px] text-[#132543]">
        {clientProjectDescription.loading ? 'Generating description…' : pdfDownload.loading ? 'Downloading…' : 'Download Report'}
      </span>
    </button>
  );

  const logoToggleAction = latestOnePager ? (
    <button
      type="button"
      className="inline-flex items-center gap-[6px] rounded-[8px] border border-[rgba(0,0,0,0.15)] bg-white px-[12px] py-[6px] font-['Inter',sans-serif] text-[13px] font-medium text-[#31394C] disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
      onClick={handleToggleShowLogo}
      disabled={toggleLogoLoading}
    >
      {showLogo ? 'Clear Logo' : 'Restore Logo'}
    </button>
  ) : null;

  const warningPill = primaryWarning ? (
    <div className="mt-4 rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-sm text-[#92400E]">
      <div className="flex items-center gap-2">
        <TriangleAlert size={16} />
        <span className="font-semibold">{warningSeverityLabel ? `${warningSeverityLabel} warning` : 'Warning'}</span>
      </div>
      <div className="mt-1">{primaryWarning.message}</div>
    </div>
  ) : null;

  if (!query.accountId || !query.introductionId) {
    return (
      <div className="mx-auto w-full max-w-[980px] p-6">
        <div className="rounded-md border border-gray-200 bg-white p-5">
          <div className="flex items-start gap-3">
            <TriangleAlert className="mt-0.5" size={18} />
            <div className="space-y-1">
              <div className="text-sm font-semibold text-gray-900">Unable to load one-pager</div>
              <div className="text-sm text-gray-700">
                {launch.loading ? 'Exchanging launch token…' : launch.error ?? 'Missing launch context. Please launch from Salesforce again.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const effectiveOnePagerType: OnePagerType =
    readOnePagerTypeOverride() ??
    onePagerVersions.value?.onePagerType ??
    latestOnePager?.onePagerType ??
    'group';

  if (effectiveOnePagerType === 'interim') {
    const interimMissingInputs = latestOnePager?.content?.missing_inputs ?? [];
    const interimPublishDisabled =
      saving.loading || publishing.loading || interimMissingInputs.length > 0 || publishDisabledBecauseAlreadyPublished;
    const interimPublishDisabledReason =
      interimMissingInputs.length > 0
        ? `Resolve missing input(s) first: ${interimMissingInputs.join(', ')}`
        : publishDisabledBecauseAlreadyPublished
          ? 'This version is already published.'
          : undefined;

    return (
      <div className="relative">
        {publishConfirmOpen && (
          <div className="fixed inset-0 z-[100]">
            <div className="absolute inset-0 bg-black/40" onClick={() => setPublishConfirmOpen(false)} />
            <div className="relative mx-auto mt-24 w-full max-w-[560px] rounded-md bg-white p-6 shadow-lg">
              <div className="text-base font-semibold text-gray-900">Confirm publish</div>
              <div className="mt-3 text-sm text-gray-700">
                {`${recordName} will be published and immediately visible to ${clientName}. Do you want to continue?`}
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  className="h-[32px] rounded-[8px] bg-white px-[13px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] border border-[rgba(0,0,0,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setPublishConfirmOpen(false)}
                  disabled={publishing.loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="h-[32px] rounded-[8px] bg-[#6bbc58] px-[24px] text-center font-['Inter',sans-serif] text-[14px] font-bold leading-[20px] tracking-[-0.1504px] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={async () => {
                    setPublishConfirmOpen(false);
                    await handleInterimPublish();
                  }}
                  disabled={publishing.loading}
                >
                  {publishing.loading ? 'Publishing…' : 'Publish'}
                </button>
              </div>
            </div>
          </div>
        )}

        {regenerateConfirmOpen && (
          <div className="fixed inset-0 z-[100]">
            <div className="absolute inset-0 bg-black/40" onClick={() => setRegenerateConfirmOpen(false)} />
            <div className="relative mx-auto mt-24 w-full max-w-[560px] rounded-md bg-white p-6 shadow-lg">
              <div className="text-base font-semibold text-gray-900">Regenerate one-pager?</div>
              <div className="mt-3 text-sm text-gray-700">
                This will re-run the AI generation using the latest inputs for this intro. Any unpublished edits to the current draft will be overwritten. The currently published version (if any) will remain visible to {clientName} until you publish the new draft.
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  className="h-[32px] rounded-[8px] bg-white px-[13px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] border border-[rgba(0,0,0,0.1)]"
                  onClick={() => setRegenerateConfirmOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="h-[32px] rounded-[8px] bg-[#364153] px-[24px] text-center font-['Inter',sans-serif] text-[14px] font-bold leading-[20px] tracking-[-0.1504px] text-white"
                  onClick={async () => {
                    setRegenerateConfirmOpen(false);
                    await handleConfirmRegenerate();
                  }}
                >
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        )}

        {sendEmailOpen && latestPublishedVersion && (
          <SendEmailModal
            accountId={query.accountId ?? ''}
            introductionId={query.introductionId ?? ''}
            versionId={latestPublishedVersion.id}
            conciergeId={sfProviderMetadata.value?.concierge?.id ?? ''}
            providerName={salesforceAccount.value?.name ?? sfProviderMetadata.value?.account?.name ?? ''}
            onClose={() => setSendEmailOpen(false)}
            onSuccess={() => {
              setToast('One-pager email will be sent shortly!');
              ensureMagicLinkUrl();
            }}
          />
        )}

        {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

        <div className="mx-auto w-full max-w-[980px] px-6 pt-6">
          <InterimOnePager
            fullName={salesforceAccount.value?.name}
            tagline={salesforceAccount.value?.tagline}
            location={salesforceAccount.value?.location}
            website={salesforceAccount.value?.website}
            salesforceFilesUrl={salesforceAccount.value?.salesforceFilesUrl}
            resumeUrl={resumeInfo.value?.resumeUrl}
            resumeFileName={resumeInfo.value?.fileName}
            resumeAttachedDate={resumeInfo.value?.attachedDate}
            content={latestOnePager?.content}
            sfAvailability={sfProviderMetadata.value?.availability}
            sfFees={sfProviderMetadata.value?.fees}
            dismissedWarnings={latestOnePager?.dismissedWarnings}
            onDismissWarning={handleDismissWarning}
            generationLoading={isPollingActive || generation.loading}
            generationError={generation.error}
            onRetryGeneration={handleRetryInterimGeneration}
            status={(latestOnePager?.status ?? '').toLowerCase() === 'published' ? 'published' : 'draft'}
            saving={saving.loading}
            savingError={saving.error}
            onSave={handleInterimSaveDraft}
            publishing={publishing.loading}
            publishingError={publishing.error}
            onPublishClick={handlePublishClick}
            publishDisabled={interimPublishDisabled}
            publishDisabledReason={interimPublishDisabledReason}
            showSendEmail={hasPublishedVersion}
            onSendEmail={() => setSendEmailOpen(true)}
            onRegenerateClick={handleRegenerateClick}
            regenerateDisabled={isPollingActive || saving.loading || publishing.loading}
            lastEditedDate={saveBarLastEditedDate}
            lastEditedTime={saveBarLastEditedTime}
            lastEditedBy={saveBarCreatedBy}
            onDownload={handleInterimDownloadPdf}
          />
          {pdfGeneration.status === 'processing' && (
            <div className="pt-2">
              <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700">
                Generating PDF...
              </div>
            </div>
          )}
          {pdfGeneration.status === 'success' && (
            <div className="pt-2">
              <div className="flex items-center gap-3 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
                <span>Published successfully.</span>
                {pdfGeneration.blobUrl && (
                  <a href={pdfGeneration.blobUrl} target="_blank" rel="noopener noreferrer" className="font-medium underline">
                    Download PDF
                  </a>
                )}
              </div>
            </div>
          )}
          {pdfGeneration.status === 'failed' && (
            <div className="pt-2">
              <div className="flex items-center gap-3 rounded-md border border-red-200 bg-white px-4 py-2 text-sm text-red-700">
                <span>Publish succeeded but PDF generation failed. You can still retry from Download or regenerate.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {publishConfirmOpen && (
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPublishConfirmOpen(false)} />
          <div className="relative mx-auto mt-24 w-full max-w-[560px] rounded-md bg-white p-6 shadow-lg">
            <div className="text-base font-semibold text-gray-900">Confirm publish</div>
            <div className="mt-3 text-sm text-gray-700">
              {`${recordName} will be published and immediately visible to ${clientName}. Do you want to continue?`}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="h-[32px] rounded-[8px] bg-white px-[13px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] border border-[rgba(0,0,0,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setPublishConfirmOpen(false)}
                disabled={publishing.loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="h-[32px] rounded-[8px] bg-[#6bbc58] px-[24px] text-center font-['Inter',sans-serif] text-[14px] font-bold leading-[20px] tracking-[-0.1504px] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={async () => {
                  setPublishConfirmOpen(false);
                  await handleConfirmPublish();
                }}
                disabled={publishing.loading}
              >
                {publishing.loading ? 'Publishing…' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {regenerateConfirmOpen && (
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setRegenerateConfirmOpen(false)} />
          <div className="relative mx-auto mt-24 w-full max-w-[560px] rounded-md bg-white p-6 shadow-lg">
            <div className="text-base font-semibold text-gray-900">Regenerate one-pager?</div>
            <div className="mt-3 text-sm text-gray-700">
              This will re-run the AI generation using the latest inputs for this intro. Any unpublished edits to the current draft will be overwritten. The currently published version (if any) will remain visible to {clientName} until you publish the new draft.
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="h-[32px] rounded-[8px] bg-white px-[13px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] border border-[rgba(0,0,0,0.1)]"
                onClick={() => setRegenerateConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="h-[32px] rounded-[8px] bg-[#364153] px-[24px] text-center font-['Inter',sans-serif] text-[14px] font-bold leading-[20px] tracking-[-0.1504px] text-white"
                onClick={async () => {
                  setRegenerateConfirmOpen(false);
                  await handleConfirmRegenerate();
                }}
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      {sendEmailOpen && latestPublishedVersion && (
        <SendEmailModal
          accountId={query.accountId ?? ''}
          introductionId={query.introductionId ?? ''}
          versionId={latestPublishedVersion.id}
          conciergeId={sfProviderMetadata.value?.concierge?.id ?? ''}
          providerName={salesforceAccount.value?.name ?? sfProviderMetadata.value?.account?.name ?? ''}
          onClose={() => setSendEmailOpen(false)}
          onSuccess={() => {
            setToast('One-pager email will be sent shortly!');
            ensureMagicLinkUrl();
          }}
        />
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

      {(sfProviderMetadata.loading || salesforceAccount.loading || launch.loading) && (
        <ProcessWaitingOverlay
          open
          title={launch.loading ? 'Exchanging launch token' : 'Fetching provider metadata'}
        />
      )}

      {!!(sfProviderMetadata.error || salesforceAccount.error || onePagerVersions.error || generation.error) && (
        <div className="mx-auto w-full max-w-[980px] p-6">
          <div className="rounded-md border border-red-200 bg-white p-5 text-sm text-red-700">
            {sfProviderMetadata.error || salesforceAccount.error || onePagerVersions.error || generation.error}
          </div>
        </div>
      )}

      <div className="mx-auto w-full" data-pdf-exclude>
        <div className="bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className={
                statusLabel.startsWith('Published')
                  ? 'rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800'
                  : 'rounded-full border border-[#364153] bg-gray-100 px-3 py-1 text-sm font-semibold text-[#364153]'
              }
            >
              {statusLabel}
            </div>
          </div>

          <div className="mt-3">
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-semibold text-gray-900"
              onClick={() => setActivityExpanded((prev) => !prev)}
            >
              {activityExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Activity
            </button>
            {activityExpanded && (
              publishedVersions.length === 0 ? (
                <div className="mt-1 text-sm text-gray-600">No published activity yet.</div>
              ) : (
                <div className="mt-2 space-y-2">
                  {publishedVersions.map((v) => {
                    const parts = v.updatedAt ? getFormattedDateParts(v.updatedAt) : { date: '', time: '' };
                    const by = v.updatedBy || v.createdBy || '';
                    const when = parts.date && parts.time ? `${parts.date} ${parts.time}` : parts.date || parts.time;
                    return (
                      <div key={v.id} className="text-sm text-gray-700">
                        <span className="font-medium">{`Publish Version ${v.version}`}</span>
                        {when ? <span>{` - ${when}`}</span> : null}
                        {by ? <span>{` - ${by}`}</span> : null}
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <Header
        companyName={headerCompanyName}
        location={headerLocation}
        websiteText={headerWebsiteText}
        servicesText={headerServicesText}
        industriesText={headerIndustriesText}
        introCallCertified={!!salesforceAccount.value?.introCallCertified}
        showLogo={showLogo}
        headerAction={pdfHeaderAction}
        contentAction={logoToggleAction}
      />

      <div className="mx-auto mt-[40px] w-full px-0" data-pdf-exclude>
        <TabControl
          activeIndex={activeTabIndex}
          tabs={[
            { label: 'Summary Recommendation' },
            { label: 'Project Criteria Fit' },
            { label: 'Relevant Project Experience' },
          ]}
          onTabClick={(index) => {
            setActiveTabIndex(index);
            if (index === 0) document.getElementById('section-snapshot')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (index === 1) document.getElementById('section-project-criteria-fit')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (index === 2)
              document
                .getElementById('section-relevant-project-experience')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      </div>

      <div className="mt-4" data-pdf-exclude>
        {warningPill}
        {pdfDownload.error ? (
          <div className="mx-auto w-full max-w-[980px] p-6">
            <div className="rounded-md border border-red-200 bg-white p-5 text-sm text-red-700">{pdfDownload.error}</div>
          </div>
        ) : null}
      </div>

      <div className="mt-6 space-y-6 pb-[88px]">
        <ProjectOverview
          title="Project Overview"
          projectDescription={sfProviderMetadata.value?.concierge?.clientProjectDescription ?? clientProjectDescription.value}
          isLoading={clientProjectDescription.loading}
          hasError={clientProjectDescription.error}
          onRetry={() => setClientProjectDescriptionRetry((prev) => prev + 1)}
          bodyHtml={projectOverviewBodyHtml}
          onSave={handleSaveProjectOverview}
          saveCommitId={saveCommitId}
        />

        <div id="section-snapshot">
          <Snapshot
            title="Snapshot - Why They're The Right Fit"
            bullets={snapshotBulletsFromApi ?? []}
            bodyHtml={snapshotBodyHtmlFromApi}
            draftHtml={draft.snapshotHtml}
            onDraftHtmlChange={(html) => setDraft((prev) => ({ ...prev, snapshotHtml: html }))}
            onCancelDraft={() => setDraft((prev) => ({ ...prev, snapshotHtml: undefined }))}
            saveCommitId={saveCommitId}
          />
        </div>

        <div className="mt-[20px] h-[2px] w-full bg-[#E8E8E8]" />

        <div id="section-why-we">
          <WhyWe
            title="Why we recommend them for your project"
            bodyHtml={whyWeBodyHtml}
            draftHtml={draft.whyWeHtml}
            onDraftHtmlChange={(html) => setDraft((prev) => ({ ...prev, whyWeHtml: html }))}
            onCancelDraft={() => setDraft((prev) => ({ ...prev, whyWeHtml: undefined }))}
            saveCommitId={saveCommitId}
          />
        </div>

        <div className="relative mt-[56px] w-full">
          <div className="absolute left-[47px] top-0 h-full w-[1px] bg-[#53BEE8]" />

          <div className="pl-[80px]">
            <TrustedBy firmNames={trustedByFirmNames} peBackedReviewQuote={trustedByPeReviewQuote} />

            <TopSupportingEvidence
              items={topSupportingEvidenceItems}
              onViewAllSourcesClick={() => {
                window.location.hash = '#section-relevant-project-experience';
              }}
            />
          </div>
        </div>

        <div id="section-project-criteria-fit" className="!mt-[96px]">
          <ProjectCriteriaFit
            rows={projectCriteriaFitRows}
            draftProviderFitByKey={draft.criteriaFitByKey}
            onDraftProviderFitChange={(next) => setDraft((prev) => ({ ...prev, criteriaFitByKey: next }))}
            onCancelDraft={() => setDraft((prev) => ({ ...prev, criteriaFitByKey: undefined }))}
            saveCommitId={saveCommitId}
          />
        </div>

        <div id="section-relevant-project-experience">
          {onePagerVersions.loading ? (
            <RelevantProjectExperienceSkeleton />
          ) : (
            <RelevantProjectExperience
              items={relevantProjectExperienceItems}
              draftItems={draft.highlightedEvidence?.map((e) => ({
                id: e.evidence_id,
                title: e.title,
                content: e.content,
                sourceId: e.source_id,
                evidenceType: e.evidence_type,
                relevanceScore: e.relevance_score,
              }))}
              onDraftItemsChange={(nextItems: RelevantProjectExperienceItem[]) => {
                setDraft((prev) => ({
                  ...prev,
                  highlightedEvidence: nextItems.map((i: RelevantProjectExperienceItem) => ({
                    evidence_id: i.id,
                    title: i.title,
                    content: i.content,
                    source_id: i.sourceId ?? '',
                    evidence_type: i.evidenceType ?? '',
                    relevance_score: i.relevanceScore ?? 0,
                  })),
                }));
              }}
              onCreateDraftVersion={handleCreateDraftVersionFromEvidence}
              onCancelDraft={() => setDraft((prev) => ({ ...prev, highlightedEvidence: undefined }))}
              saveCommitId={saveCommitId}
              pendingAddRequest={pendingIntroQualAdd ? { generation: pendingIntroQualAdd.generation, content: pendingIntroQualAdd.content } : undefined}
              onAddedFromPending={() => {
                if (pendingIntroQualAdd) {
                  setUsedIntroQualSequences((prev) => [...prev, pendingIntroQualAdd.sequence]);
                  setPendingIntroQualAdd(null);
                }
              }}
            />
          )}
        </div>

        <IntroQualifications
          items={sfProviderMetadata.value?.qualificationIntros}
          usedSequences={usedIntroQualSequences}
          onAdd={(description, sequence) => {
            introQualAddGenerationRef.current += 1;
            setPendingIntroQualAdd({ generation: introQualAddGenerationRef.current, content: description, sequence });
          }}
        />
      </div>

      <div className="mt-6" data-pdf-exclude>
        <SaveBar
          lastEditedDate={saveBarLastEditedDate}
          lastEditedTime={saveBarLastEditedTime}
          createdBy={saveBarCreatedBy}
          onSave={handleSaveDraft}
          onPublish={handlePublishClick}
          onSendEmail={() => setSendEmailOpen(true)}
          onRegenerate={handleRegenerateClick}
          showSave
          showSendEmail={hasPublishedVersion}
          showRegenerate={!!latestOnePager}
          regenerateDisabled={isPollingActive || saving.loading || publishing.loading}
          saveDisabled={saving.loading || publishing.loading}
          saveLoading={saving.loading}
          publishDisabled={saving.loading || publishing.loading || publishDisabledBecauseAlreadyPublished || clientProjectDescription.loading}
          publishLoading={publishing.loading}
          publishLabel={clientProjectDescription.loading ? 'Generating description…' : undefined}
        />
        {saving.error || publishing.error ? (
          <div className="mx-auto w-full max-w-[980px] p-4">
            <div className="rounded-md border border-red-200 bg-white p-4 text-sm text-red-700">
              {saving.error || publishing.error}
            </div>
          </div>
        ) : null}
        {pdfGeneration.status === 'processing' && (
          <div className="mx-auto w-full max-w-[980px] px-4 pt-2">
            <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700">
              Generating PDF...
            </div>
          </div>
        )}
        {pdfGeneration.status === 'success' && (
          <div className="mx-auto w-full max-w-[980px] px-4 pt-2">
            <div className="flex items-center gap-3 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
              <span>Published successfully.</span>
              {pdfGeneration.blobUrl && (
                <a
                  href={pdfGeneration.blobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline"
                >
                  Download PDF
                </a>
              )}
              <button
                type="button"
                className="rounded-md bg-green-700 px-3 py-1 text-sm font-medium text-white hover:bg-green-800"
                onClick={() => {/* TODO: send email handler */}}
              >
                Send Email
              </button>
            </div>
          </div>
        )}
        {pdfGeneration.status === 'failed' && (
          <div className="mx-auto w-full max-w-[980px] px-4 pt-2">
            <div className="flex items-center gap-3 rounded-md border border-red-200 bg-white px-4 py-2 text-sm text-red-700">
              <span>Publish succeeded but PDF generation failed. You can still send the email later.</span>
            </div>
          </div>
        )}
      </div>

      {isPollingActive && (
        <ProcessWaitingOverlay
          open
          title="Generating one-pager"
          pollCount={pollMeta.count}
          latestStatus={pollMeta.latestStatus}
        />
      )}
    </div>
  );
}
function TestToolsPage() {
  const [accountId, setAccountId] = useState('001Qq000000BkLrIAK');
  const [introductionId, setIntroductionId] = useState('a0oQq000000KjNZIA0');
  const [userId, setUserId] = useState('');
  const [requestId, setRequestId] = useState('');

  const [health, setHealth] = useState<{ loading: boolean; error?: string; value?: string }>({
    loading: true,
  });

  const [salesforceAccount, setSalesforceAccount] = useState<{
    loading: boolean;
    error?: string;
    value?: unknown;
  }>({ loading: false });

  const [preview, setPreview] = useState<{ loading: boolean; error?: string; value?: string }>({
    loading: false,
  });

  const [createGen, setCreateGen] = useState<{
    loading: boolean;
    error?: string;
    value?: unknown;
    requestId?: string;
  }>({ loading: false });

  const [genStatus, setGenStatus] = useState<{ loading: boolean; error?: string; value?: unknown }>({
    loading: false,
  });

  const [onePagerVersions, setOnePagerVersions] = useState<{
    loading: boolean;
    error?: string;
    value?: unknown;
  }>({ loading: false });

  useEffect(() => {
    setCreateGen((prev) => ({ ...prev, requestId: undefined }));
    setGenStatus({ loading: false });
  }, [accountId, introductionId, userId]);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const text = await rootService.getHealthText(controller.signal);
        setHealth({ loading: false, value: text });
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        const message = e instanceof Error ? e.message : String(e);
        setHealth({ loading: false, error: message });
      } finally {
        setHealth((prev) => ({ ...prev, loading: false }));
      }
    })();

    return () => controller.abort();
  }, []);

  const handleSalesforceAccount = async () => {
    setSalesforceAccount({ loading: true });
    try {
      const data = await salesforceService.getProviderOnePagerMetadata(accountId, introductionId);
      setSalesforceAccount({ loading: false, value: data.account });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setSalesforceAccount({ loading: false, error: message });
    }
  };

  const handlePreview = async () => {
    setPreview({ loading: true });
    try {
      const content = await generationsService.preview({
        accountId,
        introductionId,
        userId: userId || undefined,
      });
      setPreview({ loading: false, value: content });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setPreview({ loading: false, error: message });
    }
  };

  const handleCreateGeneration = async () => {
    setCreateGen({ loading: true });
    try {
      const res = await generationsService.create({
        accountId,
        introductionId,
        userId: userId || undefined,
      });

      setCreateGen({ loading: false, value: res, requestId: res.requestId });
      setRequestId(res.requestId);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setCreateGen({ loading: false, error: message });
    }
  };

  const handleGetGenerationStatus = async () => {
    if (!requestId) {
      setGenStatus({ loading: false, error: 'Enter a requestId first.' });
      return;
    }

    setGenStatus({ loading: true });
    try {
      const res = await generationsService.getStatus(requestId);
      setGenStatus({ loading: false, value: res });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setGenStatus({ loading: false, error: message });
    }
  };

  const handleListOnePagerVersions = async () => {
    setOnePagerVersions({ loading: true });
    try {
      const res = await onePagersService.listVersions(accountId, introductionId);
      setOnePagerVersions({ loading: false, value: res });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setOnePagerVersions({ loading: false, error: message });
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">API Examples</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">accountId</div>
            <input
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">introductionId</div>
            <input
              value={introductionId}
              onChange={(e) => setIntroductionId(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">userId (optional)</div>
            <input
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            />
          </label>
        </div>
      </div>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">GET / (via /api/)</h2>
        <div className="text-gray-700">{health.loading ? 'Loading…' : health.error ? health.error : health.value}</div>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">GET /api/v1/salesforce/accounts/:accountId</h2>
        <button
          type="button"
          className="px-3 py-2 rounded bg-blue-500 text-white disabled:opacity-60"
          onClick={handleSalesforceAccount}
          disabled={salesforceAccount.loading}
        >
          {salesforceAccount.loading ? 'Loading…' : 'Fetch Salesforce Account'}
        </button>
        {salesforceAccount.error && <div className="text-red-600">{salesforceAccount.error}</div>}
        {!!salesforceAccount.value && (
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto">
            {JSON.stringify(salesforceAccount.value, null, 2)}
          </pre>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">POST /api/v1/generations/preview</h2>
        <button
          type="button"
          className="px-3 py-2 rounded bg-blue-500 text-white disabled:opacity-60"
          onClick={handlePreview}
          disabled={preview.loading}
        >
          {preview.loading ? 'Loading…' : 'Run Preview'}
        </button>
        {preview.error && <div className="text-red-600">{preview.error}</div>}
        {preview.value && (
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto whitespace-pre-wrap">
            {preview.value}
          </pre>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">POST /api/v1/generations (async)</h2>
        <button
          type="button"
          className="px-3 py-2 rounded bg-blue-500 text-white disabled:opacity-60"
          onClick={handleCreateGeneration}
          disabled={createGen.loading}
        >
          {createGen.loading ? 'Loading…' : 'Create Generation'}
        </button>
        {createGen.error && <div className="text-red-600">{createGen.error}</div>}
        {!!createGen.value && (
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto">
            {JSON.stringify(createGen.value, null, 2)}
          </pre>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">GET /api/v1/generations/:requestId</h2>
        <label className="block">
          <div className="text-sm font-medium text-gray-700 mb-1">requestId</div>
          <input
            value={requestId}
            onChange={(e) => setRequestId(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
          />
        </label>
        <button
          type="button"
          className="px-3 py-2 rounded bg-blue-500 text-white disabled:opacity-60"
          onClick={handleGetGenerationStatus}
          disabled={genStatus.loading}
        >
          {genStatus.loading ? 'Loading…' : 'Get Generation Status'}
        </button>
        {genStatus.error && <div className="text-red-600">{genStatus.error}</div>}
        {!!genStatus.value && (
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto">
            {JSON.stringify(genStatus.value, null, 2)}
          </pre>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">GET /api/v1/one-pagers/:accountId/:introductionId/versions</h2>
        <button
          type="button"
          className="px-3 py-2 rounded bg-blue-500 text-white disabled:opacity-60"
          onClick={handleListOnePagerVersions}
          disabled={onePagerVersions.loading}
        >
          {onePagerVersions.loading ? 'Loading…' : 'List One-Pager Versions'}
        </button>
        {onePagerVersions.error && <div className="text-red-600">{onePagerVersions.error}</div>}
        {!!onePagerVersions.value && (
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto">
            {JSON.stringify(onePagerVersions.value, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}

function getHashRoute() {
  const hash = window.location.hash || '';
  if (hash === '#/test-tools') {
    return 'test-tools';
  }
  return 'home';
}

function App() {
  const { isLoaded, isSignedIn } = useAuth();
  const [route, setRoute] = useState<'home' | 'test-tools'>(() => getHashRoute());

  useEffect(() => {
    const onHashChange = () => setRoute(getHashRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const searchParams = new URLSearchParams(window.location.search);
  const isLegacyOnePagerQuery = !!searchParams.get('accountId') && !!searchParams.get('introductionId');
  const isOnePagerPath = window.location.pathname === ONE_PAGER_PATH || isLegacyOnePagerQuery;
  const isHomePath = window.location.pathname === '/';
  const isAdminPath = window.location.pathname.startsWith('/admin');

  if (!isLoaded) return null;

  if (route === 'test-tools') {
    return <Layout><TestToolsPage /></Layout>;
  }

  if (isOnePagerPath) {
    return <Layout><LandingPage /></Layout>;
  }

  if (isAdminPath) {
    if (!isSignedIn) {
      window.location.replace('/');
      return null;
    }
    return (
      <Layout>
        <AdminGuard>
          <AdminUsersPage />
        </AdminGuard>
      </Layout>
    );
  }

  if (isHomePath) {
    if (isSignedIn) {
      window.location.replace(ONE_PAGER_PATH);
      return null;
    }
    return <HomePage />;
  }

  window.location.replace('/');
  return null;
}

export default App;
